import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { injectIntl } from 'react-intl';

import { LoginCell, MoneyCell, BaseTable, BasicModal, ActionsButton } from 'common/components';
import TopDownDistributionPanel from 'client/partnership/_components/TopDownDistributionPanel';

import { withTheme } from 'common/styling/theme';
import noInfoImage from 'common/images/noInfo.svg';
import { EditIcon } from 'common/icons';

import UserNoInfoPanel from '../../../_components/UserNoInfoPanel';
import UserPartnerAccountEditForm from '../UserPartnerAccountEditForm';

import { staticStyles, getDynamicStyles } from './style';

const UserPartnerAccountsTable = ({
  data,
  rewardFrequencies,
  partnershipConditionsPermission,
  intl,
  partnershipConditionsList,
  userId,
  getUserPartnerAccounts,
  simplePartnerSystem,
  theme,
}) => {
  const [selectedAccount, setSelectedAccount] = useState(undefined);
  const [partnerCode, setPartnerCode] = useState(undefined);

  const openEditUserPartnerAccountModal = (accountId, partnerCode) => {
    setSelectedAccount(accountId);
    setPartnerCode(partnerCode);
  };

  const closeEditUserPartnerAccountModal = () => {
    setSelectedAccount(undefined);
    setPartnerCode(undefined);
  };

  const dynamicStyles = Object.keys(theme).length ? getDynamicStyles(theme) : ` `;

  const REWARD_FREQUENCIES = {
    dailyInstant: intl.formatMessage({ id: 'justDailyInstant' }),
    monthlyInstant: intl.formatMessage({ id: 'justMonthlyInstant' }),
    monthlyDelayed: intl.formatMessage({ id: 'justMonthlyDelayed' }),
  };

  const initialCondition = useMemo(
    () => data.find(item => item.id === selectedAccount)?.partnershipConditions ?? null,
    [data, selectedAccount]
  );
  const initialConditionId = initialCondition?.id ?? '';

  const topDownProgramOptions = useMemo(() => {
    const options = partnershipConditionsList
      .filter(condition => condition.distributionFlow === 'top_down')
      .map(condition => ({
        id: condition.id,
        title: condition.title,
      }));
    if (
      initialCondition?.distributionFlow === 'top_down' &&
      !options.some(option => String(option.id) === String(initialCondition.id))
    ) {
      options.push({
        id: initialCondition.id,
        title: initialCondition.title,
      });
    }
    return options;
  }, [initialCondition, partnershipConditionsList]);

  const assignableConditions = useMemo(() => {
    const currentCondition =
      partnershipConditionsList.find(condition => String(condition.id) === String(initialConditionId)) ||
      initialCondition;
    if (currentCondition && currentCondition.distributionFlow === 'top_down') {
      return [currentCondition];
    }
    return partnershipConditionsList.filter(condition => condition.distributionFlow !== 'top_down');
  }, [initialCondition, initialConditionId, partnershipConditionsList]);

  const initialTopDownProgramId = useMemo(
    () =>
      topDownProgramOptions.some(option => String(option.id) === String(initialConditionId))
        ? initialConditionId
        : null,
    [initialConditionId, topDownProgramOptions]
  );

  const initialFrequency = useMemo(
    () => data.find(item => item.id === selectedAccount)?.frequencyOfPayments,
    [data, selectedAccount]
  );
  const showEditColumn = partnershipConditionsPermission && !simplePartnerSystem;

  /* eslint-disable */
  const editColumn =
    showEditColumn
      ? [
          {
            Cell: ({ original: { id, partnerCode } }) => (
              <ActionsButton
                edit
                onClickFunc={e => {
                  e.stopPropagation();
                  openEditUserPartnerAccountModal(id, partnerCode);
                }}>
                <EditIcon />
              </ActionsButton>
            ),
            isPreviewCell: true,
          },
        ]
      : [];

  let advancePartnerSystemColumns;
  if (!simplePartnerSystem) {
    advancePartnerSystemColumns = [
      {
        key: 'rewardTemplate',
        Cell: ({ original: { partnershipConditions, topDownMasterPartnerCode } }) => (
          <p className="RewardType__cell">
            {topDownMasterPartnerCode
              ? `MIB ${topDownMasterPartnerCode} tree`
              : partnershipConditions
              ? partnershipConditions.title
              : '—'}
          </p>
        ),
      },
      {
        key: 'justBalance',
        Cell: ({ original: { balance, loading, loadingFailed } }) => (
          <MoneyCell inTable value={balance} loading={loading} loadingFailed={loadingFailed} />
        ),
        isPreviewCell: true,
      },
      {
        key: 'justFrequencyOfReward',
        Cell: ({ original: { frequencyOfPayments } }) => (
          <p className="RewardType__cell">{REWARD_FREQUENCIES[frequencyOfPayments] ?? (frequencyOfPayments || '—')}</p>
        ),
      },
      {
        key: 'referType',
        Cell: ({ original: { referType } }) => (
          <p className="RewardType__cell">{referType === 'refer_friend' ? 'Friend' : 'Partner'}</p>
        ),
      },
    ];
  } else {
    advancePartnerSystemColumns = [];
  }

  const columns = [
    {
      key: 'accountId',
      Cell: ({ original: { partnerCode } }) => (
        <LoginCell login={partnerCode} title={intl.formatMessage({ id: 'partnerAccount' })} inTable />
      ),
      isPreviewCell: true,
    },
    {
      key: 'rewardType',
      Cell: ({ original: { partnershipConditions } }) => (
        <p className="RewardType__cell">{partnershipConditions ? partnershipConditions.rewardType : '—'}</p>
      ),
      isPreviewCell: true,
    },
    ...advancePartnerSystemColumns,
    ...editColumn,
  ];
  /* eslint-enable */
  return (
    <div
      className={classNames('UserPartnerAccountsTable', {
        UserPartnerAccountsTable__noAccount: !data.length,
        UserPartnerAccountsTable__small: simplePartnerSystem,
        UserPartnerAccountsTable__withoutEditColumn: !showEditColumn,
      })}>
      {data.length ? (
        <div className="UserPartnerAccountsTable__body">
          <BaseTable columns={columns} data={data} isResponsive />
        </div>
      ) : (
        <UserNoInfoPanel image={noInfoImage} textId="boNoPartnerAccounts" />
      )}

      <BasicModal
        captionId="editPartnerAccountCaption"
        isOpen={partnershipConditionsPermission && !!selectedAccount}
        onRequestClose={closeEditUserPartnerAccountModal}>
        {partnershipConditionsPermission && !!selectedAccount && (
          <>
            <UserPartnerAccountEditForm
              accountId={selectedAccount}
              partnershipConditionsList={assignableConditions}
              partnerCode={+partnerCode}
              rewardFrequencies={rewardFrequencies}
              onSubmitSuccess={() => {
                closeEditUserPartnerAccountModal();
                getUserPartnerAccounts(userId);
              }}
              initialValues={{
                conditionsId: initialConditionId,
                frequencyOfPayments: initialFrequency,
              }}
            />
            <TopDownDistributionPanel
              key={selectedAccount}
              accountId={selectedAccount}
              allowCreate
              auditView
              initialProgramId={initialTopDownProgramId}
              programOptions={topDownProgramOptions}
              onPlanChange={() => getUserPartnerAccounts(userId)}
            />
          </>
        )}
      </BasicModal>

      <style jsx>{staticStyles}</style>
      <style jsx>{dynamicStyles}</style>
    </div>
  );
};

UserPartnerAccountsTable.propTypes = {
  data: PropTypes.array.isRequired,
  rewardFrequencies: PropTypes.array.isRequired,
  intl: PropTypes.object.isRequired,
  partnershipConditionsPermission: PropTypes.bool.isRequired,
  partnershipConditionsList: PropTypes.array.isRequired,
  userId: PropTypes.string.isRequired,
  getUserPartnerAccounts: PropTypes.func.isRequired,
  simplePartnerSystem: PropTypes.bool.isRequired,
  theme: PropTypes.object,
};

UserPartnerAccountsTable.defaultProps = {
  theme: {},
};

export default withTheme()(injectIntl(UserPartnerAccountsTable));
export { UserPartnerAccountsTable };
